import { Copy, Download, Link2, Share2 } from 'lucide-react'
import { showSuccessData } from '@/lib/toast'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useShareActions } from '../../hooks/use-share-action'
import { useShareImage } from '../../hooks/use-share-image'
import { SharePreviewCard } from './share-preview-card'

interface SharePreviewDialogProps {
  open: boolean
  onClose: () => void
}

export function SharePreviewDialog({ open, onClose }: SharePreviewDialogProps) {
  const isMobile = useIsMobile()
  const { buildSharePayload, exitShareMode } = useShareActions()
  const { cardRef, isGenerating, downloadImage, copyToClipboard } =
    useShareImage()

  const payload = buildSharePayload()

  const handleDownload = async () => {
    if (!payload) return
    await downloadImage(`${payload.engineTitle}-分享.png`)
  }

  const handleCopy = async () => {
    await copyToClipboard()
  }

  const handleCopyLink = () => {
    if (!payload) return
    navigator.clipboard.writeText(payload.shareUrl)
    showSuccessData('链接已复制')
  }

  const handleClose = () => {
    onClose()
    exitShareMode()
  }

  if (!payload) return null

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className='flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Share2 className='h-5 w-5' />
            分享对话
          </DialogTitle>
        </DialogHeader>

        {/* 预览区域 */}
        <div className='bg-muted/30 -mx-2 flex-1 overflow-y-auto rounded-lg p-4'>
          <div className='flex justify-center'>
            <SharePreviewCard ref={cardRef} payload={payload} />
          </div>
        </div>

        {/* 生成进度 */}
        {isGenerating && (
          <div className='text-muted-foreground flex items-center gap-2 text-sm'>
            <div className='border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent' />
            生成中...
          </div>
        )}

        {/* 操作按钮 */}
        <div className='flex gap-3 pt-2'>
          <Button
            variant='outline'
            className='flex-1'
            onClick={handleCopy}
            disabled={isGenerating}
          >
            <Copy className='mr-2 h-4 w-4' />
            复制图片
          </Button>
          <Button
            className='flex-1'
            onClick={handleDownload}
            disabled={isGenerating}
          >
            {isMobile ? (
              <>
                <Share2 className='mr-2 h-4 w-4' />
                分享
              </>
            ) : (
              <>
                <Download className='mr-2 h-4 w-4' />
                保存图片
              </>
            )}
          </Button>
        </div>

        {/* 链接复制 */}
        <div className='bg-muted/50 flex items-center gap-2 rounded-lg px-3 py-2 text-xs'>
          <Link2 className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          <span className='text-muted-foreground flex-1 truncate'>
            {payload.shareUrl}
          </span>
          <Button
            size='sm'
            variant='ghost'
            className='h-6 px-2'
            onClick={handleCopyLink}
          >
            复制
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
